import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import logo from "../assets/logo.svg";
import home from "../assets/home.svg";
import key from "../assets/key.svg";
import user from "../assets/user.svg";
import { LogOut, Building2, ArrowDownCircle, ArrowUpCircle, Hourglass, X, Menu, Redo, Link, Gift, TrendingUp, Settings, ChevronDown, ChevronRight, Banknote, PiggyBank, Ticket, Megaphone, Mail, Landmark, Activity, Users, Repeat, ArrowRightLeft, AlertTriangle, Network, Percent, Globe } from "lucide-react";
import { useDispatch } from "react-redux";
import { logout } from "../features/auth/authSlice";
import toast from "react-hot-toast";


const Sidebar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const dispatch = useDispatch()
  const location = useLocation();


  const navLinkStyle = ({ isActive }) => {
    const baseStyle = "font-medium text-xs md:text-sm flex flex-row items-center gap-2 rounded-3xl px-2 py-2";
    const activeStyle = isActive
      ? "text-[#CCF6E5] bg-[#013941]"
      : "text-[#013941]";

    return `${baseStyle} ${activeStyle}`;
  };

  const subNavLinkStyle = ({ isActive }) => {
    const baseStyle = "font-medium text-xs md:text-sm flex flex-row items-center gap-2 rounded-3xl px-2 py-2 ml-3";
    const activeStyle = isActive
      ? "text-[#CCF6E5] bg-[#013941]"
      : "text-[#013941]";

    return `${baseStyle} ${activeStyle}`;
  };

  const getIconStyle = (isActive) => {
    return isActive
      ? "filter brightness-0 invert"
      : "";
  };

  const NavGroup = ({ label, icon: Icon, paths, children }) => {
    const isChildActive = paths.some((p) => location.pathname.startsWith(p));
    const [open, setOpen] = useState(isChildActive);

    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex flex-row gap-2 items-center px-2 py-2 w-full text-xs font-medium text-left rounded-3xl md:text-sm text-[#013941]"
        >
          <Icon className="w-3 h-3 md:w-4 md:h-4" />
          <span className="flex-1">{label}</span>
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        {open && <div className="flex flex-col gap-1">{children}</div>}
      </div>
    );
  };

  const handleLogout = () => {
    // Add your logout logic here
    toast.success("Account successfully logged out")
    setTimeout(() => {
      dispatch(logout())
    }, 2000)

  };

  const NavLinks = ({ onNavigate }) => (
    <>
      <NavLink to="/dashboard" className={navLinkStyle} onClick={onNavigate}>
        {({ isActive }) => (
          <>
            <img src={home} alt="Dashboard" className={`h-3 md:h-4 ${getIconStyle(isActive)}`} />
            Dashboard
          </>
        )}
      </NavLink>

      <p className="text-[#013941] font-medium text-[10px] md:text-xs uppercase pl-2 md:pl-1 mt-2">Personal</p>

      <NavLink to="/users" className={navLinkStyle} onClick={onNavigate}>
        {({ isActive }) => (
          <>
            <img src={user} alt="Users" className={`h-3 md:h-4 ${getIconStyle(isActive)}`} />
            Consumers
          </>
        )}
      </NavLink>

      <NavGroup label="Payouts" icon={Banknote} paths={["/pending-payouts", "/failed-payouts", "/payout-review"]}>
        <NavLink to="/pending-payouts" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Hourglass className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Pending Payouts
            </>
          )}
        </NavLink>

        <NavLink to="/failed-payouts" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <AlertTriangle className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Failed Payouts
            </>
          )}
        </NavLink>

        <NavLink to="/payout-review" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <ArrowUpCircle className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Payouts Review
            </>
          )}
        </NavLink>
      </NavGroup>

      <NavGroup label="Deposits" icon={PiggyBank} paths={["/deposit-review", "/reserve-transactions"]}>
        <NavLink to="/deposit-review" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <ArrowDownCircle className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Deposits Review
            </>
          )}
        </NavLink>

        <NavLink to="/reserve-transactions" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Redo className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Reserve Transactions
            </>
          )}
        </NavLink>
      </NavGroup>

      <NavGroup label="Gift Card" icon={Gift} paths={["/gift-card-buy-orders", "/gift-card-sell-orders"]}>
        <NavLink to="/gift-card-buy-orders" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Gift className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Buy Orders
            </>
          )}
        </NavLink>

        <NavLink to="/gift-card-sell-orders" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <TrendingUp className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Sell Orders
            </>
          )}
        </NavLink>
      </NavGroup>

      <NavGroup label="Marketing" icon={Megaphone} paths={["/coupon-campaigns", "/send-product-update"]}>
        <NavLink to="/coupon-campaigns" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Ticket className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Coupon Campaigns
            </>
          )}
        </NavLink>

        <NavLink to="/send-product-update" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Mail className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Send Product Update
            </>
          )}
        </NavLink>
      </NavGroup>

      <NavGroup label="Configurations" icon={Settings} paths={["/consumer-payout-settings", "/consumer-notification-settings", "/announcements"]}>
        <NavLink to="/consumer-payout-settings" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Banknote className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Consumer Payout Settings
            </>
          )}
        </NavLink>
        <NavLink to="/consumer-notification-settings" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Mail className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Notification Settings
            </>
          )}
        </NavLink>
        <NavLink to="/announcements" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Megaphone className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Announcements
            </>
          )}
        </NavLink>
      </NavGroup>

      <p className="text-[#013941] font-medium text-[10px] md:text-xs uppercase pl-2 md:pl-1 mt-2">Business/Merchant</p>

      <NavGroup label="Payouts" icon={Banknote} paths={["/merchant-payouts"]}>
        <NavLink to="/merchant-payouts" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <ArrowUpCircle className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              All Payouts
            </>
          )}
        </NavLink>
      </NavGroup>

      <NavGroup label="Deposits" icon={PiggyBank} paths={["/business-deposit-review"]}>
        <NavLink to="/business-deposit-review" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <ArrowDownCircle className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Deposits Review
            </>
          )}
        </NavLink>
      </NavGroup>

      <NavGroup
        label="Configurations"
        icon={Settings}
        paths={["/currency-settings", "/country-settings", "/payout-partner-settings", "/crypto-networks", "/webhooks"]}
      >
        <NavLink to="/currency-settings" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Settings className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Currency Settings
            </>
          )}
        </NavLink>

        <NavLink to="/country-settings" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Globe className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Country Settings
            </>
          )}
        </NavLink>

        <NavLink to="/payout-partner-settings" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Banknote className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Payout Partner Settings
            </>
          )}
        </NavLink>

        <NavLink to="/crypto-networks" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Network className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Crypto Networks
            </>
          )}
        </NavLink>

        <NavLink to="/webhooks" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Link className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Providers Webhook
            </>
          )}
        </NavLink>
      </NavGroup>

      <p className="text-[#013941] font-medium text-[10px] md:text-xs uppercase pl-2 md:pl-1 mt-2">Treasury</p>

      <NavGroup label="Treasury" icon={Landmark} paths={["/treasury/partner-balances", "/treasury/daily-position", "/treasury/consumer-merchant-position", "/treasury/conversion-log", "/treasury/partner-conversion", "/treasury/fee-margin"]}>
        <NavLink to="/treasury/partner-balances" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Banknote className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Partner Balances
            </>
          )}
        </NavLink>

        <NavLink to="/treasury/daily-position" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Activity className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Merchant ↔ Partner
            </>
          )}
        </NavLink>

        <NavLink to="/treasury/consumer-merchant-position" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Users className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Consumer ↔ Merchant
            </>
          )}
        </NavLink>

        <NavLink to="/treasury/conversion-log" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Repeat className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Conversion Log
            </>
          )}
        </NavLink>

        <NavLink to="/treasury/partner-conversion" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <ArrowRightLeft className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Partner Conversion
            </>
          )}
        </NavLink>

        <NavLink to="/treasury/fee-margin" className={subNavLinkStyle} onClick={onNavigate}>
          {({ isActive }) => (
            <>
              <Percent className={`h-3 w-3 md:h-4 md:w-4 ${isActive ? "text-[#CCF6E5]" : "text-[#013941]"}`} />
              Fee Margin
            </>
          )}
        </NavLink>

        {/* Orphaned Payouts link hidden on request - the scheduled check/email
            is also paused (see TreasuryCronService). The page and its
            backend endpoint are untouched, just not linked from the nav. */}
      </NavGroup>
    </>
  );



  return (
    <>
      {/* ── DESKTOP SIDEBAR ── */}
      <div className="hidden md:flex bg-[#CCF6E5] w-64 px-6 py-8 border-r-2 border-r-[#B3F2D8] h-screen flex-col relative flex-shrink-0">
        <div className="flex items-center py-4 mb-8">
          <img src={logo} alt="Novacrust Logo" className="h-5 md:h-8" />
        </div>

        <nav className="flex overflow-y-auto flex-col gap-1.5">
          <NavLinks />
        </nav>

        <button
          onClick={handleLogout}
          className="flex absolute right-6 left-6 bottom-8 gap-2 items-center px-5 py-3 text-base font-medium text-red-600 rounded-3xl transition-colors duration-200 hover:bg-red-600 hover:text-white"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* ── MOBILE TOPBAR ── */}
      <div className="md:hidden">
        <div className="bg-[#CCF6E5] border-b-2 border-b-[#B3F2D8] flex items-center justify-between px-4 py-3">
          <img src={logo} alt="Novacrust Logo" className="h-6" />

          {/* dropdown */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="p-2 rounded-full text-[#013941] hover:bg-[#B3F2D8] transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />

                <div className="absolute right-0 top-full mt-3 z-20 min-w-[220px] bg-[#CCF6E5] border-2 border-[#B3F2D8] rounded-xl py-2 px-2 flex flex-col gap-1">
                  <NavLinks onNavigate={() => setMenuOpen(false)} />

                  <div className="border-t border-[#B3F2D8] mt-1 pt-1 ">
                    <button
                      onClick={() => { handleLogout(); setMenuOpen(false); }}
                      className="flex gap-2 items-center px-3 py-3 w-full text-sm font-medium text-red-500 rounded-3xl transition-colors duration-200 hover:bg-red-500 hover:text-white"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>

  );
};

export default Sidebar;